export const cppDefaultSource = `// Raw pointer allocations will be converted to smart pointers
// This demonstrates modernizing manual memory management to RAII

#include <memory>
#include <vector>
#include <string>

class Widget {
public:
    Widget(int value) : value_(value) {}
    int getValue() const { return value_; }
private:
    int value_;
};

int main() {
    // These raw pointer allocations will be transformed
    Widget* widget = new Widget(42);
    int* numbers = new int[10];
    std::string* message = new std::string("Hello");
    
    // Usage patterns
    widget->getValue();
    numbers[0] = 5;
    *message = "Updated";
    
    // This won't be matched (already smart pointer)
    auto smart_widget = std::make_unique<Widget>(100);
    
    // Complex allocation
    Widget* complex = new Widget(widget->getValue() + 10);
    
    // Manual cleanup (will need to be removed)
    delete widget;
    delete[] numbers;
    delete message;
    delete complex;
    
    return 0;
}`;

export const cppDefaultCodemod = `import { type SgRoot } from "@ast-grep/napi";
import type CPP from "@ast-grep/napi/lang/cpp";

async function transform(root: SgRoot<CPP>): Promise<string> {
  const rootNode = root.root();

  // Find raw pointer declarations with new
  const ptrDeclarations = rootNode.findAll({
    rule: {
      pattern: "$TYPE* $VAR = new $CONSTRUCTOR($$$ARGS)"
    },
  });

  // Find array allocations
  const arrayDeclarations = rootNode.findAll({
    rule: {
      pattern: "$TYPE* $VAR = new $TYPE[$SIZE]"
    },
  });

  // Find delete statements to remove
  const deleteStatements = rootNode.findAll({
    rule: {
      any: [
        { pattern: "delete $VAR" },
        { pattern: "delete[] $VAR" }
      ]
    },
  });

  const edits = [
    // Convert new allocations to make_unique
    ...ptrDeclarations.map(node => {
      const type = node.getMatch("TYPE").text();
      const varName = node.getMatch("VAR").text();
      const constructor = node.getMatch("CONSTRUCTOR").text();
      const args = node.getMatch("ARGS");
      const argsText = args ? args.text() : "";
      
      return node.replace(\`auto \${varName} = std::make_unique<\${type}>(\${argsText})\`);
    }),
    
    // Convert array allocations to vector or array
    ...arrayDeclarations.map(node => {
      const type = node.getMatch("TYPE").text();
      const varName = node.getMatch("VAR").text();
      const size = node.getMatch("SIZE").text();
      
      return node.replace(\`std::vector<\${type}> \${varName}(\${size})\`);
    }),
    
    // Remove delete statements
    ...deleteStatements.map(node => {
      return node.replace("// Automatic cleanup with smart pointers");
    })
  ];

  const newSource = rootNode.commitEdits(edits);
  return newSource;
}

export default transform;
`;

export const cppDefaultConfig = `
# Codemod Studio uses ast-grep to match and rewrite code.
# https://ast-grep.github.io/guide/rule-config.html#rule
id: modernize-raw-pointers
language: cpp
rule:
  any:
    - pattern: $TYPE* $VAR = new $CONSTRUCTOR($$$ARGS)
    - pattern: $TYPE* $VAR = new $TYPE[$SIZE]
fix: |
  auto $VAR = std::make_unique<$TYPE>($ARGS)
`.trim();
